import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { createLanguage, updateLanguage } from "@/services/api/i18n";
import type { Language, LanguageInput } from "@/services/api/i18n";
import { useLanguages } from "@/hooks/useLanguages";

interface LanguageFormProps {
  initial?: Language;
  onDone: () => void;
}

function LanguageForm({ initial, onDone }: LanguageFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(initial?.code ?? "");
  const [displayName, setDisplayName] = useState(initial?.display_name ?? "");
  const [nativeName, setNativeName] = useState(initial?.native_name ?? "");
  const [direction, setDirection] = useState<"ltr" | "rtl">(initial?.direction ?? "ltr");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [isPrimary, setIsPrimary] = useState(initial?.is_primary ?? false);
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);

  const save = useMutation({
    mutationFn: async () => {
      const payload: LanguageInput = {
        code,
        display_name: displayName,
        native_name: nativeName,
        direction,
        is_active: isActive,
        is_primary: isPrimary,
        sort_order: sortOrder,
      };
      if (initial) {
        await updateLanguage(initial.code, payload);
      } else {
        await createLanguage(payload);
      }
    },
    onSuccess: () => {
      toast.success(t("translations.languageSaved"));
      void queryClient.invalidateQueries({ queryKey: ["languages"] });
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="language-code">{t("translations.code")}</Label>
          <Input
            id="language-code"
            value={code}
            disabled={Boolean(initial)}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="language-sort">{t("translations.sortOrder")}</Label>
          <Input
            id="language-sort"
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(Number(event.target.value))}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="language-display">{t("translations.displayName")}</Label>
          <Input
            id="language-display"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="language-native">{t("translations.nativeName")}</Label>
          <Input
            id="language-native"
            value={nativeName}
            onChange={(event) => setNativeName(event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t("translations.direction")}</Label>
        <Select value={direction} onValueChange={(value) => setDirection(value as "ltr" | "rtl")}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ltr">LTR</SelectItem>
            <SelectItem value="rtl">RTL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={isActive} onCheckedChange={setIsActive} />
          {t("translations.active")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={isPrimary} onCheckedChange={setIsPrimary} />
          {t("translations.primary")}
        </label>
      </div>
      {isPrimary ? (
        <p className="text-xs text-muted-foreground">
          Only one language can be primary — this replaces any existing primary.
        </p>
      ) : null}

      <Button onClick={() => save.mutate()} disabled={save.isPending || !code || !displayName}>
        {t("common.save")}
      </Button>
    </div>
  );
}

/** Admin: the data-driven locale registry (add a language without a rebuild). */
export function LanguagesPage() {
  const { t } = useTranslation();
  const languages = useLanguages();
  const [editing, setEditing] = useState<Language | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>).
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.languages")}</h1>
          <p className="text-sm text-muted-foreground">{t("translations.languagesSubtitle")}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>{t("translations.addLanguage")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("translations.addLanguage")}</DialogTitle>
              <DialogDescription>{t("translations.languagesSubtitle")}</DialogDescription>
            </DialogHeader>
            <LanguageForm onDone={() => setCreateOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {ghostRecord ? (
        <AuditGhostRecord
          entityType={audit.entityType ?? "language"}
          entityId={audit.recordId}
        />
      ) : null}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("translations.code")}</TableHead>
              <TableHead>{t("translations.nativeName")}</TableHead>
              <TableHead>{t("translations.displayName")}</TableHead>
              <TableHead>{t("translations.direction")}</TableHead>
              <TableHead>{t("translations.status")}</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {languages.isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              </TableRow>
            ) : (
              languages.data?.map((language) => (
                <TableRow
                  key={language.code}
                  className={
                    String(language.id) === audit.recordId ? "audit-record-highlight" : undefined
                  }
                >
                  <TableCell className="font-mono text-sm">{language.code}</TableCell>
                  <TableCell>{language.native_name}</TableCell>
                  <TableCell>{language.display_name}</TableCell>
                  <TableCell>{language.direction}</TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      {language.is_primary ? (
                        <Badge variant="default">{t("translations.primary")}</Badge>
                      ) : null}
                      {!language.is_active ? (
                        <Badge variant="secondary">{t("translations.inactive")}</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Dialog open={editing?.code === language.code} onOpenChange={(open) => setEditing(open ? language : null)}>
                      <DialogTrigger asChild>
                        <Button size="icon" variant="ghost" onClick={() => setEditing(language)} aria-label={`Edit ${language.code}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t("common.edit")} — {language.code}</DialogTitle>
                        </DialogHeader>
                        <LanguageForm initial={language} onDone={() => setEditing(null)} />
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
